import {
  createRef,
  useCallback,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import {
  Box,
  DataTableAddRow,
  Icon,
  MenuItem,
  renderDefaultGroupActions,
  resolveVisibleGroups,
  Text,
  toastManager,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  UNASSIGNED_GROUP_KEY,
  type DataTableGroupActionsContext,
  type DataTableGroupType,
  type DataTableRowDragConfig,
  type DataTableSection,
  type EntityItemConfig,
  type MenuAnchor,
  useDataTableState,
} from "@salestrak/ui";

import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import type { IssueRow, StageOption } from "#app/lib/data";
import type { useAppData } from "#app/lib/data";
import type { useTableFilters } from "#app/lib/hooks/use-table-filters";
import { EntityTag } from "#app/components/entity";
import { iconSourceToName } from "#app/lib/entity-visuals";
import { AGE_STATUS_CONFIG, type AgeStatus } from "#app/lib/age-status";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { AnalyticsContext, AnalyticsView, KanbanEvent, trackEvent } from "#app/lib/analytics";
import type { useTeamPool } from "#app/components/entity-selectors";
import { useCountedGroupItems } from "#app/hooks/issue-list/use-counted-group-items";
import {
  buildGroupWhere,
  ISSUE_GROUP_SERVER_KEY,
  UNASSIGNED_DIMENSION_LABEL,
  type IssueGroupDimension,
} from "#app/components/issues/issue-grouping";
import { StageGroupChip } from "#app/components/issues/issue-group-title";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { useKanbanMutations } from "#app/hooks/kanban/use-kanban-mutations";
import { useBoardReassign } from "#app/hooks/kanban/use-board-reassign";
import type { useIssueView } from "#app/components/issue-view";
import { StageInlineEditor } from "#app/components/stages/stage-inline-editor";
import { useStageEditActions } from "#app/components/stages/use-stage-edit-actions";
import { useStagesDialogActions } from "#app/lib/stages-dialog-context";
import type { useIssueTypes } from "#app/lib/hooks/use-issue-types";
import type { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { scopeWhere, type IssueScope } from "#app/lib/filters/issue-scope";
import { useAutomateFromView } from "#app/hooks/automations/use-automate-from-view";
import { AutomateStageMenuItem } from "#app/components/automations/automate-stage-menu-item";
import { applyCurrentUserFilter, combineWhereConditions } from "#app/lib/query-utils";
import { useGroupStates } from "#app/lib/hooks/group-query-provider";
import { viewArchivedWhere, type viewStageWhere } from "#app/lib/view-config";

// The table's group field keys differ from the shared grouping module's
// dimension names (`assignees` vs `assignee`); map them so the table and board
// resolve selectors + server group keys through the one shared source.
export const FIELD_TO_DIMENSION: Record<string, IssueGroupDimension> = {
  stage: "stage",
  assignees: "assignee",
  team: "team",
  teams: "teams",
  age: "age",
  issueType: "issueType",
  workflow: "workflow",
};

export function groupWhereForField(
  field: string,
  key: string,
): Record<string, unknown> | undefined {
  const dimension = FIELD_TO_DIMENSION[field];
  return dimension ? buildGroupWhere(dimension, key) : undefined;
}

function serverGroupKeyForField(field: string): string | undefined {
  const dimension = FIELD_TO_DIMENSION[field];
  return dimension ? ISSUE_GROUP_SERVER_KEY[dimension] : undefined;
}

/**
 * Everything the issue list does per GROUP: the lazily activated per-group
 * query states and their totals, the group `where` resolver the bulk actions
 * and the per-group providers share, the DataTable groups themselves, the
 * group title, band, footer and actions renderers, row drag between and
 * within groups, and the one table-level stage menu (Follow up cycle…).
 */
export function useIssuesTableGrouping({
  scope,
  workflowId,
  isSharedScope,
  isGrouped,
  activeGroupConfig,
  groupByField,
  groupSortDirection,
  tableFilters,
  screenFilterWhere,
  searchWhere,
  searchValue,
  panelFilterCount,
  showArchived,
  stageWhere,
  isCurrentUserFilterApplied,
  where,
  availableStages,
  workflows,
  workspaceStages,
  memberItems: rosterItems,
  teamItems,
  issueTypes,
  handOrder,
  openCreateIssue,
}: {
  scope: IssueScope;
  workflowId: string | undefined;
  isSharedScope: boolean;
  isGrouped: boolean;
  activeGroupConfig: ReturnType<typeof useDataTableState<IssueRow>>["activeGroupConfig"];
  groupByField: string;
  groupSortDirection: ReturnType<typeof useDataTableState<IssueRow>>["groupSortDirection"];
  tableFilters: ReturnType<typeof useTableFilters>;
  screenFilterWhere: Record<string, unknown> | undefined;
  searchWhere: Record<string, unknown> | undefined;
  searchValue: string;
  panelFilterCount: number;
  showArchived: boolean;
  stageWhere: ReturnType<typeof viewStageWhere>;
  isCurrentUserFilterApplied: boolean;
  where: Record<string, unknown>;
  availableStages: StageOption[];
  workflows: ReturnType<typeof useAppData>["workflows"];
  workspaceStages: ReturnType<typeof useWorkspaceData>["stages"];
  memberItems: EntityItemConfig[];
  teamItems: ReturnType<typeof useTeamPool>["pool"]["items"];
  issueTypes: ReturnType<typeof useIssueTypes>;
  handOrder: boolean;
  openCreateIssue: ReturnType<typeof useIssueView>["openCreateIssue"];
}) {
  const { states, registerState, getGroupProps } = useGroupStates<IssueRow>();

  // Track which groups have been activated by the DataTable's lazy loading.
  // When onGroupActivate fires, we add the key here to mount a GroupQueryProvider.
  const [activatedKeys, setActivatedKeys] = useState<Set<string>>(new Set());
  const handleGroupActivate = useCallback((key: string) => {
    setActivatedKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  // Inline stage rename/recolor from a stage group's header — the same
  // actions the board's stage column headers offer, mounted ONCE here, not
  // per group. The editing group's key; null while every header is at rest.
  const [editingStageId, setEditingStageId] = useState<string | null>(null);
  const {
    canEditStages,
    renameStage: handleRenameStage,
    recolorStage: handleRecolorStage,
  } = useStageEditActions("table_group");
  const { openStagesDialog } = useStagesDialogActions();
  const exitStageEditing = useCallback(() => setEditingStageId(null), []);

  // Reset activated keys (and an in-flight stage edit) when the group-by
  // field changes
  const prevGroupByFieldRef = useRef(groupByField);
  if (prevGroupByFieldRef.current !== groupByField) {
    prevGroupByFieldRef.current = groupByField;
    setActivatedKeys(new Set());
    setEditingStageId(null);
  }

  const groupBaseWhere = useMemo(() => {
    const effectiveTableWhere =
      isGrouped && activeGroupConfig?.filterKey
        ? tableFilters.getWhereExcluding(activeGroupConfig.filterKey)
        : tableFilters.where;
    // Scope + archived scoping must be repeated here: the ungrouped `where`
    // carries them, but grouped queries are built from this base instead.
    // Without them, group wheres like "assignee hasSome [x]" match issues
    // outside the scope, producing rows whose stages aren't in availableStages
    // (rendered as "Unknown").
    const conditions: Record<string, unknown>[] = [scopeWhere(scope)];
    const archivedWhere = viewArchivedWhere({ showArchived }, screenFilterWhere, effectiveTableWhere);
    if (archivedWhere) conditions.push(archivedWhere);
    if (stageWhere) conditions.push(stageWhere);
    if (searchWhere) conditions.push(searchWhere);
    if (effectiveTableWhere) conditions.push(effectiveTableWhere);
    if (screenFilterWhere) conditions.push(screenFilterWhere);
    return combineWhereConditions(conditions);
  }, [
    scope,
    showArchived,
    stageWhere,
    isGrouped,
    activeGroupConfig,
    searchWhere,
    tableFilters,
    screenFilterWhere,
  ]);

  // --- Single query for all group totals ---
  // Use groupBaseWhere (group field filter stripped) + currentUser filter
  // so counts match the per-group data queries.
  const groupCountsDbField = serverGroupKeyForField(groupByField);
  const groupCountsWhere = useMemo(() => {
    if (!isGrouped || !groupCountsDbField) return undefined;
    return applyCurrentUserFilter(
      groupBaseWhere,
      isCurrentUserFilterApplied ?? false,
    );
  }, [
    isGrouped,
    groupCountsDbField,
    groupBaseWhere,
    isCurrentUserFilterApplied,
  ]);
  const { counts: groupCountsRecord, ready: groupCountsReady } =
    useIssueGroupCounts({
      where: groupCountsWhere,
      groupBy: groupCountsDbField,
    });

  const dimension = FIELD_TO_DIMENSION[groupByField];
  const {
    memberItems,
    teamItems: allTeamItems,
    countedItems,
  } = useCountedGroupItems(
    dimension,
    groupCountsRecord,
    rosterItems,
    teamItems,
  );

  const groupConfig = useMemo(() => {
    if (!activeGroupConfig || !countedItems) return activeGroupConfig;
    const configured = activeGroupConfig.groups ?? [];
    const known = new Set(configured.map((g) => g.key));
    const extra = countedItems
      .filter((item) => !known.has(item.id))
      .map((item) => ({ key: item.id, label: item.title }));
    if (extra.length === 0) return activeGroupConfig;
    // Alphabetical with the roster's bands, Unassigned still last.
    const members = [
      ...configured.filter((g) => g.key !== UNASSIGNED_GROUP_KEY),
      ...extra,
    ].sort((a, b) => a.label.localeCompare(b.label));
    const unassigned = configured.filter((g) => g.key === UNASSIGNED_GROUP_KEY);
    return { ...activeGroupConfig, groups: [...members, ...unassigned] };
  }, [activeGroupConfig, countedItems]);

  const visibleGroups = useMemo(() => {
    if (!isGrouped || !groupConfig) return [];
    return resolveVisibleGroups(
      groupConfig,
      tableFilters.filters,
      groupSortDirection,
    );
  }, [isGrouped, groupConfig, tableFilters.filters, groupSortDirection]);

  // --- getGroupWhere: resolves a group key to its full where clause ---
  const getGroupWhere = useCallback(
    (groupKey: string): Record<string, unknown> => {
      if (!isGrouped || groupKey === "__all__") {
        return where;
      }
      return applyCurrentUserFilter(
        combineWhereConditions(
          [groupBaseWhere, groupWhereForField(groupByField, groupKey)].filter(
            Boolean,
          ) as Record<string, unknown>[],
        ),
        isCurrentUserFilterApplied ?? false,
      );
    },
    [
      isGrouped,
      where,
      groupBaseWhere,
      groupByField,
      isCurrentUserFilterApplied,
    ],
  );

  const isGroupCountsReady = !isGrouped || groupCountsReady;

  const totalIssueCount = useMemo(() => {
    if (!isGrouped) return states.get("__all__")?.total ?? 0;
    return Object.values(groupCountsRecord).reduce(
      (sum, count) => sum + count,
      0,
    );
  }, [isGrouped, groupCountsRecord, states]);

  const hasOtherActiveFilters =
    Boolean(searchValue) ||
    Boolean(tableFilters.where) ||
    panelFilterCount > 0;

  const isTrulyEmpty = useMemo(() => {
    if (hasOtherActiveFilters || isCurrentUserFilterApplied) return false;
    // A stage-grouped view never blanks to the global zero state: the stage
    // bands ARE its zero state — the pipeline skeleton, each stage with an
    // add-row in the stage it targets (open, or a closed band when the view
    // collapses empty stages). The
    // centered card stays for flat lists and non-stage groupings, where
    // there is no canonical structure to show instead.
    if (isGrouped && groupByField === "stage") return false;
    if (!isGrouped && (states.get("__all__")?.loading ?? true)) return false;
    if (isGrouped && !isGroupCountsReady) return false;
    return totalIssueCount === 0;
  }, [
    groupByField,
    hasOtherActiveFilters,
    isCurrentUserFilterApplied,
    isGroupCountsReady,
    isGrouped,
    states,
    totalIssueCount,
  ]);

  // Filters (not a search) that match nothing: the zero-count bands would
  // say only "0" four times, so the screen says why and offers the way out.
  const isFilteredEmpty = useMemo(() => {
    if (!hasOtherActiveFilters || searchValue) return false;
    if (!isGrouped && (states.get("__all__")?.loading ?? true)) return false;
    if (isGrouped && !isGroupCountsReady) return false;
    return totalIssueCount === 0;
  }, [
    hasOtherActiveFilters,
    searchValue,
    isGroupCountsReady,
    isGrouped,
    states,
    totalIssueCount,
  ]);

  const isCurrentUserEmpty = useMemo(() => {
    if (!isCurrentUserFilterApplied || hasOtherActiveFilters) return false;
    if (!isGrouped && (states.get("__all__")?.loading ?? true)) return false;
    if (isGrouped && !isGroupCountsReady) return false;
    return totalIssueCount === 0;
  }, [
    hasOtherActiveFilters,
    isCurrentUserFilterApplied,
    isGroupCountsReady,
    isGrouped,
    states,
    totalIssueCount,
  ]);

  const stageById = useMemo(() => {
    const map = new Map<string, StageOption>();
    for (const s of availableStages) map.set(s.stageId, s);
    return map;
  }, [availableStages]);
  const workflowById = useMemo(() => {
    const map = new Map<string, (typeof workflows)[number]>();
    for (const wf of workflows) map.set(wf.workflowId, wf);
    return map;
  }, [workflows]);
  const workflowIdByStage = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of workspaceStages) {
      if (s.workflowId) map.set(s.stageId, s.workflowId);
    }
    return map;
  }, [workspaceStages]);
  // The workflow band — a group title when the list groups by workflow (the
  // same rung every other group title stands on: the team tag's default), a
  // section band when its stage groups sit under their workflow (one size
  // up, a heading over headings).
  const renderWorkflowTitle = useCallback(
    (workflowKey: string, size?: "md") => {
      const wf = workflowById.get(workflowKey);
      if (!wf) return workflowKey;
      return (
        <Box direction="row" align="center" gap="sm" overflow="hidden">
          <EntityTag
            entity={{
              kind: "workflow",
              id: wf.workflowId,
              workflow: { name: wf.name ?? "" },
            }}
            size={size}
            interactive={false}
          />
        </Box>
      );
    },
    [workflowById],
  );
  const renderWorkflowSectionTitle = useCallback(
    (workflowKey: string) => renderWorkflowTitle(workflowKey, "md"),
    [renderWorkflowTitle],
  );

  const groups = useMemo<DataTableGroupType<IssueRow>[]>(() => {
    if (isGrouped && !isGroupCountsReady) return [];
    if (!isGrouped) {
      return [
        {
          key: "__all__",
          label: "All Projects",
          totalCount: states.get("__all__")?.total,
          ...getGroupProps("__all__"),
        },
      ];
    }
    // A hand-off list shows what the viewer holds, so a group nothing landed
    // in is noise there (the roster behind it is the whole workspace's);
    // a workflow's own list keeps its empty stages as pipeline information.
    const shown = isSharedScope
      ? visibleGroups.filter((g) => (groupCountsRecord[g.key] ?? 0) > 0)
      : visibleGroups;
    // Every group filtered away is a list with nothing in it, not a list with
    // no groups: hand DataTable one settled empty ungrouped group so it paints
    // the empty state instead of a blank body.
    if (shown.length === 0 && visibleGroups.length > 0) {
      return [
        {
          key: "__all__",
          label: "All Projects",
          data: [] as IssueRow[],
          totalCount: 0,
        },
      ];
    }
    // Stage groups across workflows sit under their workflow's section band.
    const sectionFor = (key: string): DataTableSection | undefined => {
      if (groupByField !== "stage" || workflowId) return undefined;
      const wfId = workflowIdByStage.get(key);
      const wf = wfId ? workflowById.get(wfId) : undefined;
      return wf ? { key: wf.workflowId, label: wf.name ?? "" } : undefined;
    };
    return shown.map((g) => {
      const section = sectionFor(g.key);
      // If the group has been activated and has data from GroupQueryProvider, use it
      const stateData = states.get(g.key);
      if (activatedKeys.has(g.key) && stateData) {
        return {
          key: g.key,
          label: g.label,
          section,
          ...getGroupProps(g.key),
        };
      }
      // Placeholder — DataTable will wire up lazy activation via onGroupActivate.
      // `totalCount` is stated explicitly, ZERO INCLUDED: the counts query is a
      // GROUP BY, so it returns no row for a group holding nothing, and a
      // placeholder with no count reads to DataTable as "rows may be on the way"
      // — which is what kept empty groups open by default.
      return {
        key: g.key,
        label: g.label,
        section,
        data: [] as IssueRow[],
        totalCount: groupCountsRecord[g.key] ?? 0,
        placeholder: true,
        dataOffset: 0,
      };
    });
  }, [
    isGrouped,
    isGroupCountsReady,
    isSharedScope,
    visibleGroups,
    states,
    getGroupProps,
    activatedKeys,
    groupCountsRecord,
    groupByField,
    workflowId,
    workflowIdByStage,
    workflowById,
  ]);

  // "Automate stage" on a stage group — shared gate.
  const { canAutomate: canAutomateStage } = useAutomateFromView(workflowId);
  const userById = useMemo(
    () => new Map(memberItems.map((i) => [i.id, i] as const)),
    [memberItems],
  );
  const teamById = useMemo(
    () => new Map(allTeamItems.map((i) => [i.id, i] as const)),
    [allTeamItems],
  );
  const issueTypeById = useMemo(() => {
    const map = new Map<string, (typeof issueTypes)[number]>();
    for (const t of issueTypes) map.set(t.issueTypeId, t);
    return map;
  }, [issueTypes]);
  // --- Group title renderer ---
  const renderGroupTitle = useCallback(
    (groupKey: string) => {
      if (groupKey === UNASSIGNED_GROUP_KEY) {
        const dimension = FIELD_TO_DIMENSION[groupByField];
        return (
          <Text size="sm" variant="muted" weight="medium">
            {dimension ? UNASSIGNED_DIMENSION_LABEL[dimension] : "Unassigned"}
          </Text>
        );
      }

      if (groupByField === "stage") {
        const stage = stageById.get(groupKey);
        if (!stage) return groupKey;
        if (editingStageId === stage.stageId) {
          // The edit row takes a fixed footprint so the ✓ sits at a stable
          // distance whatever the stage name's length.
          return (
            <Box width={300} maxWidth="100%">
              <StageInlineEditor
                stage={stage}
                onRename={handleRenameStage}
                onRecolor={handleRecolorStage}
                onOpenSettings={
                  workflowId ? () => openStagesDialog(workflowId) : undefined
                }
                onExit={exitStageEditing}
              />
            </Box>
          );
        }
        if (!canEditStages) {
          // min-w-0, not overflow-hidden: the chip pulls itself 4px left onto
          // the row pies' axis, and a clipping wrapper would shear that edge
          // off. The flex row still shrinks, so the name truncates.
          return (
            <Box direction="row" align="center" gap="sm" minWidth={0}>
              <StageGroupChip stage={stage} />
            </Box>
          );
        }
        // Press-to-rename: the chip IS the trigger, so its tint lifts on hover
        // and the press target is exactly the label it renames.
        return (
          <Tooltip>
            <TooltipTrigger
              render={
                <StageGroupChip
                  stage={stage}
                  render={
                    <Box
                      as="button"
                      aria-label={`Rename ${stage.name}`}
                      onClick={() => setEditingStageId(stage.stageId)}
                    />
                  }
                />
              }
            />
            <TooltipPopup side="top">Edit stage name</TooltipPopup>
          </Tooltip>
        );
      }

      if (groupByField === "assignees") {
        const user = userById.get(groupKey);
        if (user) {
          return (
            <Box direction="row" align="center" gap="sm" overflow="hidden">
              <EntityTag
                entity={{
                  kind: "user",
                  id: user.id,
                  user: { name: user.title, profileImage: user.avatar?.src ?? null },
                }}
                interactive={false}
              />
            </Box>
          );
        }
        return groupKey;
      }

      if (groupByField === "team" || groupByField === "teams") {
        const team = teamById.get(groupKey);
        if (team) {
          return (
            <Box direction="row" align="center" gap="sm" overflow="hidden">
              <EntityTag
                entity={{
                  kind: "team",
                  id: team.id,
                  team: {
                    teamName: team.title,
                    icon: {
                      icon: iconSourceToName(team.avatar?.icon),
                      color: team.avatar?.color ?? null,
                    },
                  },
                }}
                interactive={false}
              />
            </Box>
          );
        }
        return groupKey;
      }

      if (groupByField === "age") {
        const ageInfo = AGE_STATUS_CONFIG[groupKey as AgeStatus];
        if (ageInfo) {
          return (
            <Box direction="row" align="center" gap="sm" overflow="hidden">
              {/* The config's semantic `background` token doubles as the
                  icon color. */}
              <Icon name="clock" size="md" color={ageInfo.background} />
              <Box fill="flex" shrink>
                <Text size="sm" weight="medium" truncate={1}>
                  {ageInfo.label}
                </Text>
              </Box>
            </Box>
          );
        }
        return groupKey;
      }

      if (groupByField === "workflow") return renderWorkflowTitle(groupKey);

      if (groupByField === "issueType") {
        const it = issueTypeById.get(groupKey);
        if (!it) return groupKey;
        return (
          <Box direction="row" align="center" gap="sm" overflow="hidden">
            <EntityTag
              entity={{ kind: "issueType", id: it.issueTypeId, issueType: it }}
              size="md"
              interactive={false}
            />
          </Box>
        );
      }

      return groupKey;
    },
    [
      groupByField,
      stageById,
      userById,
      teamById,
      issueTypeById,
      renderWorkflowTitle,
      editingStageId,
      canEditStages,
      workflowId,
      handleRenameStage,
      handleRecolorStage,
      openStagesDialog,
      exitStageEditing,
    ],
  );

  const { moveIssueToStage, reorderIssue } = useKanbanMutations();
  const { reassignIssues } = useBoardReassign();
  const rowDrag = useMemo<DataTableRowDragConfig<IssueRow> | undefined>(() => {
    if (groupByField === "assignees" || groupByField === "teams") {
      const dimension = groupByField === "assignees" ? "assignee" : "teams";
      const byId = dimension === "assignee" ? userById : teamById;
      return {
        reorder: false,
        canDrag: (issue) => issue.viewerCanEdit,
        canDrop: (groupKey) =>
          groupKey === UNASSIGNED_GROUP_KEY || byId.has(groupKey),
        onDrop: async ({ row: issue, fromGroupKey, toGroupKey }) => {
          if (fromGroupKey === toGroupKey) {
            toastManager.add({
              type: "info",
              title: "Reordering is off in this grouping",
              description:
                "Drop a Project on another group to reassign it. Group by Stage to arrange Projects by hand.",
            });
            return;
          }
          try {
            await reassignIssues({
              rows: [issue],
              dimension,
              fromKey: fromGroupKey,
              toKey: toGroupKey,
            });
            trackEvent({
              context: AnalyticsContext.Kanban,
              event: KanbanEvent.DragReassign,
              data: { view: AnalyticsView.List, dimension, count: 1 },
            });
            // The row can land in a folded or unloaded group and drop out of
            // sight, so the toast says where it went.
            toastManager.add({
              type: "success",
              title:
                toGroupKey === UNASSIGNED_GROUP_KEY
                  ? dimension === "assignee"
                    ? "Project unassigned"
                    : "Project removed from all teams"
                  : `Project assigned to ${byId.get(toGroupKey)?.title ?? "group"}`,
            });
          } catch (error) {
            toastMutationError(error, "Failed to reassign Project");
          }
        },
      };
    }
    if (groupByField !== "stage" || !workflowId) return undefined;
    return {
      getSortValue: (issue) => issue.sortOrder,
      reorder: handOrder,
      canDrag: (issue) => issue.viewerCanEdit,
      canDrop: (groupKey) => stageById.has(groupKey),
      onDrop: async ({
        row: issue,
        fromGroupKey: fromStageId,
        toGroupKey: toStageId,
        position,
        upperNeighborId,
        lowerNeighborId,
      }) => {
        if (fromStageId === toStageId && !handOrder) {
          toastManager.add({
            type: "info",
            title: "Reordering is off while sorted",
            description:
              "Projects follow the Sort by order in each stage. Clear Sort by to arrange them by hand. Moving a Project to another stage still works.",
          });
          return;
        }
        try {
          if (fromStageId !== toStageId) {
            await moveIssueToStage({
              issue,
              fromStageId,
              toStageId,
              // A sorted list has no slot to keep: the row joins the stage's
              // tail and the sort places it.
              newSortOrder: handOrder ? position : Number.MAX_SAFE_INTEGER,
              upperNeighborKey: upperNeighborId,
            });
            trackEvent({
              context: AnalyticsContext.Kanban,
              event: KanbanEvent.DragMoveStage,
              data: { view: AnalyticsView.List, count: 1 },
            });
          } else {
            await reorderIssue({
              issue,
              stageId: toStageId,
              upperNeighborKey: upperNeighborId,
              lowerNeighborKey: lowerNeighborId,
              newSortOrder: position,
            });
            trackEvent({
              context: AnalyticsContext.Kanban,
              event: KanbanEvent.DragReorder,
              data: { view: AnalyticsView.List, count: 1 },
            });
          }
        } catch (error) {
          toastMutationError(error, "Failed to move Project");
        }
      },
    };
  }, [
    groupByField,
    workflowId,
    handOrder,
    moveIssueToStage,
    reorderIssue,
    reassignIssues,
    stageById,
    userById,
    teamById,
  ]);

  // A stage group's title owns its own press (rename trigger / editor), so
  // only the caret toggles it. Every other dimension keeps the whole-title
  // toggle.
  const groupTitleInteractive = useCallback(
    (groupKey: string) =>
      groupByField === "stage" && canEditStages && stageById.has(groupKey),
    [groupByField, canEditStages, stageById],
  );

  // One create call for every per-group affordance (the header "+" and the
  // footer row). A stage group lands the project in that stage and an
  // Assigned Team group starts it with that team; every other grouping
  // creates into the workflow.
  const openCreateInGroup = useCallback(
    (groupKey: string) => {
      if (!workflowId) return;
      const stageId =
        groupByField === "stage" && stageById.has(groupKey)
          ? groupKey
          : undefined;
      const teamId =
        groupByField === "teams" && groupKey !== UNASSIGNED_GROUP_KEY
          ? groupKey
          : undefined;
      openCreateIssue({ workflowId, stageId, teamId, source: "list_view" });
    },
    [workflowId, groupByField, stageById, openCreateIssue],
  );

  const [cycleStageId, setCycleStageId] = useState<string | null>(null);
  const groupMenuTriggerRefs = useRef(
    new Map<string, RefObject<HTMLDivElement | null>>(),
  );
  // One ref object per group for its lifetime, so the default menu's wrapper
  // receives a stable ref across re-renders.
  const groupMenuTriggerRef = useCallback((groupKey: string) => {
    const existing = groupMenuTriggerRefs.current.get(groupKey);
    if (existing) return existing;
    const created = createRef<HTMLDivElement>();
    groupMenuTriggerRefs.current.set(groupKey, created);
    return created;
  }, []);
  const cycleStage = useMemo(() => {
    const stage = cycleStageId ? stageById.get(cycleStageId) : undefined;
    return stage && workflowId ? { ...stage, workflowId } : null;
  }, [stageById, cycleStageId, workflowId]);
  // A VIRTUAL anchor: its rect is read on every positioning pass, so the popup
  // opens on the "…" and follows the table's scroll (the group header is
  // sticky, so the corner moves).
  const cycleAnchor = useMemo<MenuAnchor>(() => {
    const resolve = () =>
      cycleStageId
        ? (groupMenuTriggerRefs.current.get(cycleStageId)?.current ?? null)
        : null;
    return {
      get contextElement() {
        return resolve() ?? undefined;
      },
      getBoundingClientRect: () =>
        resolve()?.getBoundingClientRect() ?? new DOMRect(),
    };
  }, [cycleStageId]);

  // Stable reference: groupActions feeds the memoized per-group sub-tables,
  // so an inline arrow here would re-render every group on every render.
  const renderGroupActions = useCallback(
    (ctx: DataTableGroupActionsContext<IssueRow>) =>
      renderDefaultGroupActions({
        ...ctx,
        // Quick-add in the band, beside the "…" — same flow as the group's
        // footer row, revealed on the same group hover.
        addAction: workflowId
          ? {
              label: "Add Project",
              onClick: () => openCreateInGroup(ctx.groupKey),
            }
          : undefined,
        menuTriggerRef: groupMenuTriggerRef(ctx.groupKey),
        // Stage groups lead with the stage's own actions (the reference group
        // menus put Rename/Edit first): Rename enters the header's inline
        // editor and Edit stages opens the workflow's stage manager dialog,
        // both for viewers who can edit stages; Follow up cycle… opens the
        // Stage's pin menu for everyone, which gates its own rows — the same
        // set the board's column menu offers.
        prepend:
          groupByField === "stage" &&
          workflowId &&
          stageById.has(ctx.groupKey) ? (
            <>
              {canEditStages ? (
                <>
                  <MenuItem onClick={() => setEditingStageId(ctx.groupKey)}>
                    <Icon name="pencil" size="md" color="muted" />
                    <Text size="sm">Rename</Text>
                  </MenuItem>
                  <MenuItem onClick={() => openStagesDialog(workflowId)}>
                    <Icon name="settings" size="md" color="muted" />
                    <Text size="sm">Edit stages</Text>
                  </MenuItem>
                </>
              ) : null}
              <MenuItem onClick={() => setCycleStageId(ctx.groupKey)}>
                <Icon name={PROPERTY_ICON.followUp} size="md" color="muted" />
                <Text size="sm">Follow up cycle…</Text>
              </MenuItem>
            </>
          ) : undefined,
        // Only grouped by stage: the group key IS the stage id, so
        // offer "Automate stage" seeded with the workflow + this stage.
        append:
          canAutomateStage &&
          groupByField === "stage" &&
          workflowId &&
          stageById.get(ctx.groupKey) ? (
            <AutomateStageMenuItem
              workflowId={workflowId}
              stageId={ctx.groupKey}
            />
          ) : undefined,
      }),
    [
      canAutomateStage,
      canEditStages,
      groupByField,
      workflowId,
      stageById,
      openCreateInGroup,
      openStagesDialog,
      groupMenuTriggerRef,
    ],
  );

  // "+ Add Project" as a real row under each group (the reference add-row
  // pattern) — stage groups pre-select their stage in the create flow. Empty
  // groups render it too: the footer is what keeps them expandable (a null
  // return would leave them inert collapsed references).
  const renderGroupFooter = useCallback(
    (groupKey: string) => {
      if (!workflowId) return null;
      return (
        <DataTableAddRow
          label="Add Project"
          onClick={() => openCreateInGroup(groupKey)}
        />
      );
    },
    [workflowId, openCreateInGroup],
  );

  return {
    states,
    registerState,
    activatedKeys,
    handleGroupActivate,
    visibleGroups,
    groupBaseWhere,
    groupCountsRecord,
    isGroupCountsReady,
    getGroupWhere,
    isTrulyEmpty,
    isFilteredEmpty,
    isCurrentUserEmpty,
    groups,
    renderGroupTitle,
    renderWorkflowTitle: renderWorkflowSectionTitle,
    groupTitleInteractive,
    renderGroupActions,
    renderGroupFooter,
    rowDrag,
    cycleStage,
    cycleAnchor,
    setCycleStageId,
  };
}
